import {
  computePolygonArea,
  isPointInPolygon,
} from '@shared/helpers/geometry/geometry.helpers';
import { isPointInsideStoreySolidOccupancy } from '@shared/helpers/design/shape.helpers';
import {
  getStoreyElevation,
  getStoreyElevationRange,
} from '@shared/helpers/design/storey.helpers';
import type { Point2D } from '@shared/models/geometry.model';
import type { Shape, Storey } from '@shared/models/design/storey.model';

/**
 * How a storey volume answers a ray hit.
 * - `solid-occupancy`: floor plate minus empty cutouts (sibling building pick).
 * - `solid-footprint`: outer solid polygon including holes (storey switch / draft).
 */
export type StoreyRayHitMode = 'solid-occupancy' | 'solid-footprint';

type StoreyRayHitOptions = {
  mode?: StoreyRayHitMode;
};

/**
 * Camera ray through a screen point, expressed in local metres (x east, y
 * south). The horizontal position is affine in elevation, so two ground
 * samples describe the whole ray.
 */
export type BuildingPickRay = {
  /** Ground position of the ray at elevation 0. */
  origin: Point2D;
  /** Horizontal movement per metre of elevation. */
  perMeter: Point2D;
};

/** Three-dimensional local-metre ray used by the exact facade projection. */
export type BuildingVolumeRay = {
  origin: readonly [number, number, number];
  direction: readonly [number, number, number];
};

/** Elevation samples per storey when walking a ray through a volume. */
const STOREY_SAMPLE_COUNT = 8;
/** Bisection steps used to sharpen the entry elevation between two samples. */
const ENTRY_REFINE_STEPS = 6;

/**
 * Build a {@link BuildingPickRay} from the ray's ground positions at two
 * elevations.
 * @param atZero Ray position at elevation 0
 * @param atReference Ray position at {@link referenceElevation}
 * @param referenceElevation Elevation of the second sample, in metres
 */
export const createBuildingPickRay = (
  atZero: Point2D,
  atReference: Point2D,
  referenceElevation: number,
): BuildingPickRay => {
  if (!referenceElevation) return { origin: atZero, perMeter: [0, 0] };
  return {
    origin: atZero,
    perMeter: [
      (atReference[0] - atZero[0]) / referenceElevation,
      (atReference[1] - atZero[1]) / referenceElevation,
    ],
  };
};

/**
 * Horizontal position of {@link ray} at {@link elevation}.
 */
export const getRayPointAtElevation = (
  ray: BuildingPickRay,
  elevation: number,
): Point2D => [
  ray.origin[0] + ray.perMeter[0] * elevation,
  ray.origin[1] + ray.perMeter[1] * elevation,
];

/**
 * Highest elevation at which {@link ray} passes through the volume of
 * {@link storeys}, i.e. through the building as it appears on screen rather
 * than only over its ground footprint.
 *
 * Sampling from the roof down means the first hit is the surface nearest the
 * camera, which is what decides picking between overlapping buildings.
 * @param ray Camera ray in local metres
 * @param storeys Storeys of one building
 * @returns Hit elevation in metres, or undefined when the ray misses
 */
export const getBuildingRayHitElevation = (
  ray: BuildingPickRay,
  storeys: Storey[] | undefined,
): number | undefined => {
  if (!storeys?.length) return undefined;

  let best: number | undefined;
  for (const storey of storeys) {
    // Sibling building pick: empty courtyards must not count as hits (DEV-3843).
    const hit = getStoreyRayHitElevation(ray, storeys, storey, {
      mode: 'solid-occupancy',
    });
    if (hit !== undefined && (best === undefined || hit > best)) best = hit;
  }
  return best;
};

/**
 * True when {@link point} hits this storey for the given {@link mode}.
 */
const isPointInsideStoreyForRayHit = (
  point: Point2D,
  storey: Storey,
  solids: Shape[],
  mode: StoreyRayHitMode,
): boolean => {
  if (mode === 'solid-footprint') {
    return solids.some((shape) => isPointInPolygon(point, shape.points));
  }
  return isPointInsideStoreySolidOccupancy(point, storey);
};

/** Elevation where a ray first enters one storey's volume. */
export const getStoreyRayHitElevation = (
  ray: BuildingPickRay,
  storeys: Storey[],
  storey: Storey,
  options: StoreyRayHitOptions = {},
): number | undefined => {
  const solids = storey.shapes?.filter((shape) => shape.cutout !== true);
  if (!solids?.length) return undefined;
  const mode = options.mode ?? 'solid-occupancy';
  const base = getStoreyElevation(storeys, storey);
  const top = base + storey.height;
  const isInsideAt = (elevation: number) =>
    isPointInsideStoreyForRayHit(
      getRayPointAtElevation(ray, elevation),
      storey,
      solids,
      mode,
    );

  let outsideAbove: number | undefined;
  for (let step = 0; step <= STOREY_SAMPLE_COUNT; step += 1) {
    const elevation = top - ((top - base) * step) / STOREY_SAMPLE_COUNT;
    if (!isInsideAt(elevation)) {
      outsideAbove = elevation;
      continue;
    }
    // Sharpen the wall entry between this hit and the last miss above it.
    let inside = elevation;
    let outside = outsideAbove;
    for (
      let refine = 0;
      outside !== undefined && refine < ENTRY_REFINE_STEPS;
      refine += 1
    ) {
      const middle = (inside + outside) / 2;
      if (isInsideAt(middle)) inside = middle;
      else outside = middle;
    }
    return inside;
  }
  return undefined;
};

/**
 * Storey whose volume the ray meets first, i.e. nearest to the camera.
 * Cutout interiors count so a pitched click in a hole stays on that storey
 * instead of falling through to the floor below (DEV-3843).
 */
export const pickStoreyByRay = (
  ray: BuildingPickRay,
  storeys: Storey[],
): Storey | undefined => {
  let best: Storey | undefined;
  let bestElevation = -Infinity;
  for (const storey of storeys) {
    const elevation = getStoreyRayHitElevation(ray, storeys, storey, {
      mode: 'solid-footprint',
    });
    if (elevation === undefined || elevation <= bestElevation) continue;
    best = storey;
    bestElevation = elevation;
  }
  return best;
};

/** Highest or lowest intersected floor, independent of camera pitch. */
export const pickStoreyByVolumeClick = (
  ray: BuildingPickRay,
  storeys: Storey[],
  options: { order?: 'highest' | 'lowest' } = {},
): Storey | undefined => {
  const hits = storeys.filter(
    (storey) =>
      getStoreyRayHitElevation(ray, storeys, storey, {
        mode: 'solid-footprint',
      }) !== undefined,
  );
  const sorted = hits.sort(
    (a, b) => getStoreyElevation(storeys, a) - getStoreyElevation(storeys, b),
  );
  return options.order === 'lowest' ? sorted[0] : sorted.at(-1);
};

type BuildingRayCandidate = {
  buildingId: string;
  storeys: Storey[] | undefined;
};

/**
 * Building whose volume the ray meets first, i.e. the one nearest the camera
 * when several buildings overlap under the pointer.
 * @param ray Camera ray in local metres
 * @param candidates Buildings to test
 */
export const pickBuildingByRay = (
  ray: BuildingPickRay,
  candidates: BuildingRayCandidate[],
): string | undefined => {
  let bestId: string | undefined;
  let bestElevation = -Infinity;
  for (const candidate of candidates) {
    const elevation = getBuildingRayHitElevation(ray, candidate.storeys);
    if (elevation === undefined || elevation <= bestElevation) continue;
    bestElevation = elevation;
    bestId = candidate.buildingId;
  }
  return bestId;
};

const RAY_EPSILON = 1e-8;

const getHorizontalFaceHitDistance = (
  ray: BuildingVolumeRay,
  elevation: number,
  storey: Storey,
  solids: Shape[],
  mode: StoreyRayHitMode,
): number | undefined => {
  const dz = ray.direction[2];
  if (Math.abs(dz) < RAY_EPSILON) return undefined;
  const distance = (elevation - ray.origin[2]) / dz;
  if (distance < 0) return undefined;
  const point: Point2D = [
    ray.origin[0] + ray.direction[0] * distance,
    ray.origin[1] + ray.direction[1] * distance,
  ];
  return isPointInsideStoreyForRayHit(point, storey, solids, mode)
    ? distance
    : undefined;
};

const getWallHitDistance = (
  ray: BuildingVolumeRay,
  start: Point2D,
  end: Point2D,
  bottom: number,
  top: number,
): number | undefined => {
  const segment: Point2D = [end[0] - start[0], end[1] - start[1]];
  const denominator =
    ray.direction[0] * segment[1] - ray.direction[1] * segment[0];
  if (Math.abs(denominator) < RAY_EPSILON) return undefined;
  const offset: Point2D = [start[0] - ray.origin[0], start[1] - ray.origin[1]];
  const distance =
    (offset[0] * segment[1] - offset[1] * segment[0]) / denominator;
  const alongSegment =
    (offset[0] * ray.direction[1] - offset[1] * ray.direction[0]) / denominator;
  if (
    distance < 0 ||
    alongSegment < -RAY_EPSILON ||
    alongSegment > 1 + RAY_EPSILON
  ) {
    return undefined;
  }
  const elevation = ray.origin[2] + ray.direction[2] * distance;
  return elevation >= bottom - RAY_EPSILON && elevation <= top + RAY_EPSILON
    ? distance
    : undefined;
};

/** Nearest intersection distance with one extruded storey volume. */
export const getStoreyVolumeRayHitDistance = (
  ray: BuildingVolumeRay,
  storeys: Storey[],
  storey: Storey,
  options: StoreyRayHitOptions = {},
): number | undefined => {
  const solids = storey.shapes.filter(
    (shape) => shape.cutout !== true && shape.points.length >= 3,
  );
  if (!solids.length) return undefined;
  const mode = options.mode ?? 'solid-occupancy';
  const { bottom, top } = getStoreyElevationRange(storeys, storey);
  const hits = [
    getHorizontalFaceHitDistance(ray, bottom, storey, solids, mode),
    getHorizontalFaceHitDistance(ray, top, storey, solids, mode),
    ...solids.flatMap((shape) =>
      shape.points.map((start, index) =>
        getWallHitDistance(
          ray,
          start,
          shape.points[(index + 1) % shape.points.length] ?? start,
          bottom,
          top,
        ),
      ),
    ),
  ].filter((distance): distance is number => distance !== undefined);
  return hits.length ? Math.min(...hits) : undefined;
};

/** Nearest intersection distance with any storey in a building. */
export const getBuildingVolumeRayHitDistance = (
  ray: BuildingVolumeRay,
  storeys: Storey[] | undefined,
): number | undefined => {
  if (!storeys?.length) return undefined;
  const hits = storeys
    .map((storey) =>
      getStoreyVolumeRayHitDistance(ray, storeys, storey, {
        mode: 'solid-occupancy',
      }),
    )
    .filter((distance): distance is number => distance !== undefined);
  return hits.length ? Math.min(...hits) : undefined;
};

/**
 * Storey whose rendered volume is met first by a three-dimensional ray.
 * Cutout interiors count toward that storey (same as {@link pickStoreyByRay}).
 */
export const pickStoreyByVolumeRay = (
  ray: BuildingVolumeRay,
  storeys: Storey[],
  order?: 'highest' | 'lowest',
): Storey | undefined => {
  let best: Storey | undefined;
  let nearest = Number.POSITIVE_INFINITY;
  for (const storey of storeys) {
    const distance = getStoreyVolumeRayHitDistance(ray, storeys, storey, {
      mode: 'solid-footprint',
    });
    if (distance === undefined) continue;
    if (order && best) {
      const elevation = getStoreyElevation(storeys, storey);
      const bestElevation = getStoreyElevation(storeys, best);
      if (
        order === 'highest'
          ? elevation <= bestElevation
          : elevation >= bestElevation
      )
        continue;
    } else if (!order && distance >= nearest) continue;
    nearest = distance;
    best = storey;
  }
  return best;
};

/** Building whose rendered volume is met first by a three-dimensional ray. */
export const pickBuildingByVolumeRay = (
  ray: BuildingVolumeRay,
  candidates: BuildingRayCandidate[],
): string | undefined => {
  let bestId: string | undefined;
  let nearest = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const distance = getBuildingVolumeRayHitDistance(ray, candidate.storeys);
    if (distance === undefined || distance >= nearest) continue;
    nearest = distance;
    bestId = candidate.buildingId;
  }
  return bestId;
};

/**
 * Solid on {@link storey} under {@link point}, or the first solid when the
 * point only hits a wall (outside every floor polygon).
 * When several solids contain the point (outer ring + courtyard house), the
 * smallest wins so a click in the hole selects the house (DEV-3843).
 */
export const pickSolidShapeOnStorey = (
  storey: Storey,
  point: Point2D,
): Shape | undefined => {
  const solids = storey.shapes.filter(
    (shape) => shape.cutout !== true && (shape.points?.length ?? 0) >= 3,
  );
  const containing = solids.filter((shape) =>
    isPointInPolygon(point, shape.points),
  );
  if (!containing.length) return solids[0];
  // Prefer the tightest footprint when outer and courtyard house overlap.
  return containing.reduce((best, candidate) =>
    computePolygonArea(candidate.points) < computePolygonArea(best.points)
      ? candidate
      : best,
  );
};

export type StoreyVolumeRayHit = {
  storey: Storey;
  shape: Shape;
  point: readonly [number, number, number];
  distance: number;
};

/** Nearest storey surface hit, including the exact local 3D hit point. */
export const getStoreyVolumeRayHit = (
  ray: BuildingVolumeRay,
  storeys: Storey[],
): StoreyVolumeRayHit | undefined => {
  const storey = pickStoreyByVolumeRay(ray, storeys);
  if (!storey) return undefined;
  const distance = getStoreyVolumeRayHitDistance(ray, storeys, storey, {
    mode: 'solid-footprint',
  });
  if (distance === undefined) return undefined;
  const point = [
    ray.origin[0] + ray.direction[0] * distance,
    ray.origin[1] + ray.direction[1] * distance,
    ray.origin[2] + ray.direction[2] * distance,
  ] as const;
  const shape = pickSolidShapeOnStorey(storey, [point[0], point[1]]);
  return shape ? { storey, shape, point, distance } : undefined;
};
